import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { TOKEN_KEY } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=none, obj=user
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If returning from Google OAuth, let AuthCallback establish the session first.
    if (typeof window !== "undefined" && (window.location.hash || "").includes("session_id=")) {
      setLoading(false);
      return () => {};
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) sessionStorage.setItem(TOKEN_KEY, data.token);
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) sessionStorage.setItem(TOKEN_KEY, data.token);
    setUser(data);
    return data;
  }, []);

  // Passwordless sign-in for guest checkout: the just-OTP-verified mobile becomes
  // (or logs into) a buyer account. Returns the user, with `created` when new.
  const guestSession = useCallback(async (phone, name) => {
    const { data } = await api.post("/auth/guest-session", { phone, name });
    if (data.token) sessionStorage.setItem(TOKEN_KEY, data.token);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Logout API can fail if network is down or session already expired.
      // We still clear local state so the UI reflects the user's intent.
      console.warn("Logout request failed:", e?.message || e);
    }
    sessionStorage.removeItem(TOKEN_KEY);
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, guestSession, logout, setUser }),
    [user, loading, login, register, guestSession, logout]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
